// React / Gatsby --------------------------------------------------
import React, { useRef, useEffect, useState } from 'react';

// Plugins ---------------------------------------------------------
import { motion, animate, useAnimation, useAnimatedState } from 'framer-motion'

// Layout ----------------------------------------------------------
import AniLink from "gatsby-plugin-transition-link/AniLink"
import { Layout } from '../components/layout'

// Data Hooks ------------------------------------------------------
import * as Context from '../components/context'

// Components ------------------------------------------------------
import { Banner, BannerContent } from '../components/banner'
import { Branding } from '../components/nav'
import { Arrow } from '../components/ui'
import { Logo } from '../components/symbols'
import { useEase } from '../components/motion'

// Chapter Function ------------------------------------------------
export default function Home() {

	const[pageLoaded, setPageLoaded] = useState(false)
	const triggerPageLoaded = () => setPageLoaded(false ? true : true)

  	return (
		<Context.Template.Provider
			value={{
				pageTitle: "",
				pageID: "home",
				navTheme: "primary",
				scrollTheme: "red",
				pageFormat: "standard",
			}}
		>
			{ !pageLoaded &&
				<IntroLoader pageLoaded={triggerPageLoaded} />
			}
			<Layout>
				<IntroSlides pageLoaded={pageLoaded} />
			</Layout>
		</Context.Template.Provider>
  )
}

export const IntroLoader = (props) => {
	const handlePageLoaded = () => props.pageLoaded(true);

	const barControls = useAnimation()
	const loaderControls = useAnimation()
	const containerControls = useAnimation()
	const loadtime = 4

	useEffect(() => {
		const sequence = async () => {
			await barControls.start( barVar.finish )
			await loaderControls.start( loaderVar.finish )
			await containerControls.start( containerVar.finish )
			await handlePageLoaded()
		}
		sequence()

	}, [barControls, containerControls, loaderControls])

	const containerVar = {
		start: {
			opacity: 1,
		},
		finish: {
			opacity: 0,
			transition: {
				duration: 2,
				ease: useEase("easeOutExpo"),
			},
		},
	}

	const loaderVar = {
		start: {
			opacity: 1,
		},
		finish: {
			opacity: 0,
			transition: {
				duration: 1,
				ease: useEase("easeOutExpo")
			}
		}
	}

	const barVar = {
		start: {
			scaleX: 0,
		},
		finish: {
			scaleX: 1,
			transition: {
				duration: loadtime,
				ease: useEase("easeInExpo"),
			},
		},
	}

	return (
		<motion.div
			className="introLoadingContainer"
			variants={containerVar}
			initial="start"
			animate={containerControls}
		>
			<Logo animate={true} />
			<motion.div
				className="pageLoader"
				variants={loaderVar}
				initial="start"
				animate={loaderControls}
			>
				<PageLoaderCounter
					from={0}
					to={100}
					duration={loadtime}
				/>
				<motion.div
					className="pageLoaderBar"
					variants={barVar}
					initial="start"
					animate={barControls}
				/>
			</motion.div>
		</motion.div>
	)
}

export const PageLoaderCounter = (props) => {
	const nodeRef = useRef()
   const controls = useAnimation()


   useEffect(() => {
      const node = nodeRef.current

		const count = animate(props.from, props.to, {
			duration: props.duration ? props.duration : 20,
			ease: useEase("easeInExpo"),
			onUpdate(value) {
				node.textContent = value.toFixed(0)
			},
      })
      count.stop;

		controls.start(
         counterVariant.on
      )
   }, [controls])

   const counterVariant = {
		off: {
			color: "#EB4100",
		},
		on: {
			color: "#1BC1D1",
			transition: {
				duration: props.duration,
				ease: useEase("easeInExpo"),
			},
		},
   }

   const classes = `pageloadingCounter`

   return (
      <motion.div
         className={classes}
         ref={nodeRef}
         variants={counterVariant}
         initial='off'
         animate={controls}
      />
   )
}

export const IntroSlides = (props) => {
	const [sequence, setSequence] = useState(false)
	const triggerSequence = () => setSequence(false ? true : true )

	const slideOneControls = useAnimation()
	const slideTwoControls = useAnimation()
	const slideThreeControls = useAnimation()

	useEffect(() => {

		const sequence = async () => {
			if ( props.pageLoaded ) {
				await slideOneControls.start(
					slideVar.fadeIn
				)
				await slideOneControls.start(
					slideVar.fadeOut
				)
				await slideTwoControls.start(
					slideVar.fadeIn
				)
				await slideTwoControls.start(
					slideVar.fadeOut
				)
				triggerSequence()
				await slideThreeControls.start(
					slideVar.fadeIn,
				)

			}
		}
		sequence()
	}, [slideOneControls, slideTwoControls, slideThreeControls, props.pageLoaded])

	const slideVar = {
		initial: {
			opacity: 0,
			y: '50vh'
		},
		fadeIn: {
			opacity: 1,
			y: 0,
			transition: {
				duration: 0.5,
				ease: useEase('easeOutExpo')
			},
		},
		fadeOut: {
			opacity: 0,
			y: '-50vh',
			transition: {
				delay: 2,
				duration: 0.5,
				ease: useEase('easeInExpo')
			}
		}
	}

	return (
		<div className="introSlide-container">
			<motion.div
				className="introSlide slideOne"
				variants={slideVar}
				initial="initial"
				animate={slideOneControls}
			>
				<div className="masterplate"></div>
				<div className="presents">presents</div>
			</motion.div>

			<motion.div
				className="introSlide slideTwo"
				variants={slideVar}
				initial="initial"
				animate={slideTwoControls}
			>
				<div className='yir'>a year in review</div>
			</motion.div>

			<motion.div
				className="introSlide slideThree"
				variants={slideVar}
				initial="initial"
				animate={slideThreeControls}
			>
				<TitleLogo loaded={sequence} />
			</motion.div>

		</div>
	)
}

export const TitleLogo = (props) => {
	const containerVar = {
		out: {
			opacity: 0.9,
		},
		in: {
			opacity: 1,
			transition: {
				duration: 4,
				staggerChildren: 0.3,
				delayChildren: 0.5,
				ease: useEase('easeOutExpo')
			}
		}
	}

	const redVar = {
		out: {
			opacity: 0,
			x: '-100%',
		},
		in: {
			opacity: 1,
			x: 0,
			transition: {
				duration: 1.4,
			}
		}
	}
	const cyanVar = {
		out: {
			opacity: 0,
			x: "100%",
		},
		in: {
			opacity: 1,
			x: 0,
			transition: {
				duration: 1,
			},
		},
	}

	const topVar = {
		out: {
			opacity: 0,
			y: "-100%",
		},
		in: {
			opacity: 1,
			y: 0,
			transition: {
				duration: 1,
			},
		},
	}

	const bottomVar = {
		out: {
			opacity: 0,
			y: "100%",
		},
		in: {
			opacity: 1,
			y: 0,
			transition: {
				duration: 1.4,
			},
		},
	}

	const arrowVar = {
		out: {
			opacity: 0,
		},
		in: {
			opacity: 1,
			transition: {
				delay: 3,
				duration: 2,
			}
		}
	}
	return (
		<motion.div
			className="title_container"
			variants={containerVar}
			initial="out"
			animate={props.loaded ? "in" : "out"}
		>
			<div className="title">
				<motion.div
					className="title_top"
					variants={topVar}
					initial="out"
				>
					Twenty defining moments from
				</motion.div>
				<div className="twentytwenty">
					<svg
						xmlns="http://www.w3.org/2000/svg"
						width="154.43"
						height="48.62"
						viewBox="0 0 154.43 48.62"
					>
						<motion.g id="red" variants={redVar} initial="out">
							<path
								fill="red"
								d="M36.59,30.52V47H0L13.08,21.14l-12-7.4C4.23,5.28,10.9,0,19.55,0,29.26,0,36,6.41,36,15.13c0,6.4-4.69,12.15-9.78,15.39Z"
							/>
							<path
								fill="red"
								d="M41,23.85C41,10.11,51.12,0,65,0S88.84,10.11,88.84,23.85s-10.11,24-23.78,24C51.12,47.82,41,37.72,41,23.85Z"
							/>
						</motion.g>
						<motion.g id="cyan" variants={cyanVar} initial="out">
							<path
								fill="aqua"
								d="M102.19,31.32V47.83H65.59L78.67,21.94l-12-7.4C69.82,6.08,76.49.8,85.15.8c9.71,0,16.44,6.41,16.44,15.13,0,6.4-4.69,12.15-9.77,15.39Z"
							/>
							<path
								fill="aqua"
								d="M106.61,24.65c0-13.74,10.11-23.85,24-23.85s23.84,10.11,23.84,23.85-10.1,24-23.78,24C116.72,48.62,106.61,38.52,106.61,24.65Z"
							/>
						</motion.g>
					</svg>
				</div>
				<motion.div
					className="title_bottom"
					variants={bottomVar}
					initial="out"
				>
					( And the people who shaped them )
				</motion.div>
			</div>
			<motion.div variants={arrowVar} initial='out'>
				<AniLink
					cover
					to="chapter-01"
					hex="#ff0000"
					activeClassName="current-page"
					direction="up"
					duration={1.6}
				>
					<Arrow direction="right" theme="dark" />
				</AniLink>
			</motion.div>
		</motion.div>
	)
}
